import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from 'react-redux';

import FormOptionCard from '@anwb/poncho/components/form-option-card';
import { PanelStandard } from '@anwb/poncho/components/panel';
import Tooltip from '@anwb/poncho/components/tooltip';
import Typography from '@anwb/poncho/components/typography';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { Option } from 'types';
import { getAvailableOptionsByUpgrade } from 'utils';

import { setOptionsAndAccessoires, setServices } from '../../../../../store/actions';
import {
	chosenOptionsSelector,
	chosenServicesSelector,
	chosenUpgradeSelector,
	isOccasionSelector,
	leaseCarDataIdSelector,
	leaseCarOptionsSelector,
	leaseCarServicesSelector,
} from '../../../../../store/selectors';

import { getAvailableOptionsForOccasion, getDifferenceInSelection } from './utils';
import { customOptionChangeGA4TrackEvent, customTooltipTaggingEvent } from './utils/datalayerEvent';

export const Options = () => {
	const options = useSelector(leaseCarOptionsSelector);
	const services = useSelector(leaseCarServicesSelector);
	const carId = useSelector(leaseCarDataIdSelector);
	const selectedOptions = useSelector(chosenOptionsSelector);
	const selectedServices = useSelector(chosenServicesSelector);
	const selectedUpgrade = useSelector(chosenUpgradeSelector);
	const isOccasion = useSelector(isOccasionSelector);

	const [availableOptions, setAvailableOptions] = useState<Option[]>([]);
	const [key, setKey] = useState<string>(
		selectedUpgrade ? selectedUpgrade.id.toString() : carId || ''
	);
	const [currentValue, setCurrentValue] = useState<string[]>([
		...selectedOptions.map((sel) => `option_${sel.id}`),
		...selectedServices.map((sel) => `service_${sel.id}`),
	]);

	const dispatch = useDispatch();
	const gaFunnelInfo = useGaFunnelInfo();

	const resetOptionsAndServicesIfUpgradeChanged = (upgrade: number) => {
		if (upgrade.toString() !== key) {
			dispatch(setOptionsAndAccessoires([]));
			dispatch(setServices([]));
			setCurrentValue([]);
		}
	};

	// New car
	useEffect(() => {
		if (isOccasion) {
			return;
		}

		const upgrade = selectedUpgrade ? selectedUpgrade.id : Number(carId);
		resetOptionsAndServicesIfUpgradeChanged(upgrade);
		setAvailableOptions(getAvailableOptionsByUpgrade(options, upgrade));
		setKey(upgrade.toString());

		// should only run when the user selects another upgrade
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [carId, dispatch, options, selectedUpgrade, isOccasion]);

	// Occasion
	useEffect(() => {
		if (isOccasion) {
			setAvailableOptions(getAvailableOptionsForOccasion(options));
		}
	}, [isOccasion, options]);

	const handleChange = (value: string | string[]) => {
		if (
			typeof value !== 'string' &&
			currentValue.length !== value.length &&
			currentValue !== value
		) {
			const { diff, added } = getDifferenceInSelection(currentValue, value);
			const changedOption = options.find((x) => x.id.toString() === diff?.replace('option_', ''));
			const changedService = services.find(
				(x) => x.id.toString() === diff?.replace('service_', '')
			);

			customOptionChangeGA4TrackEvent(changedOption || changedService, added, gaFunnelInfo);

			setCurrentValue(value);
			const serviceObjects = services.filter((service) => value.includes(`service_${service.id}`));
			const optionObjects = options.filter((option) => value.includes(`option_${option.id}`));
			dispatch(setServices(serviceObjects));
			dispatch(setOptionsAndAccessoires(optionObjects));
		}
	};

	const getTooltipForOption = (option: Option) => (
		<Tooltip
			dataTest={`option-${option.id}-tooltip`}
			customTaggingEvent={(_?: string, isOpen?: boolean) => {
				customTooltipTaggingEvent(option.description, isOpen, gaFunnelInfo);
			}}
		>
			<ReactMarkdown>{option.optionContent || ''}</ReactMarkdown>
		</Tooltip>
	);

	if (availableOptions.length < 1 && services.length < 1) return null;

	return (
		<PanelStandard data-test="options" hasOuterSpacing={false}>
			<Typography variant="component-title">Opties en accessoires</Typography>
			<FormOptionCard
				columns={2}
				key={`lease-car-options-${key}`}
				inputType="checkbox"
				name="lease-car-options"
				dataTest="lease-car-options"
				onChange={handleChange}
				preselectedOptions={currentValue}
			>
				{availableOptions.map((option) => (
					<FormOptionCard.Item
						key={option.id}
						label={option.description}
						value={`option_${option.id}`}
						subTitle={`€ ${option.monthPrice} per maand`}
						{...(option.optionContent && {
							tooltip: getTooltipForOption(option),
						})}
						subTitleSize="small"
					/>
				))}
				{services.map((service) => (
					<FormOptionCard.Item
						key={service.id}
						label={service.description}
						value={`service_${service.id}`}
						subTitle={`€ ${service.price} per maand`}
						subTitleSize="small"
					/>
				))}
			</FormOptionCard>
		</PanelStandard>
	);
};
